import { useState, useEffect } from 'react';
import { Switch, useLocation, Route, Redirect } from 'react-router-dom';
import { Header, Spinner } from '@weareopenr/component-library';
import AdminAppSidebar from 'src/components/AdminAppSidebar';
import styles from './Layout.module.scss';
import { Dashboard } from '../../pages/Dashboard/Dashboard';
import { Brand } from 'src/pages/Brand/Brand';
import { Users } from 'src/pages/Users/Users';
import { Roles } from 'src/pages/Roles/Roles';
import { User } from 'src/pages/User/User';
import { useAuthWrapper } from '@weareopenr/authkit';
import { usePermissions, useUserTracking } from 'src/hooks';
import ProtectedRoute from 'src/components/ProtectedRoute';

const Layout = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { pathname } = useLocation();
  const { user, signOut, isLoading } = useAuthWrapper();
  const { canViewUsersAndRoles, canViewRoles } = usePermissions();
  const { identifyUser } = useUserTracking();

  useEffect(() => {
    if (user?.id) {
      identifyUser(user.id, {
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
      });
    }
  }, [user, identifyUser]);

  const handleDrawerToggle = () => setIsSidebarOpen((prev: boolean) => !prev);
  const closeSideBar = () => setIsSidebarOpen(false);

  if (isLoading)
    return (
      <div className={styles.spinner}>
        <Spinner size="xlarge" colour="rainbow" />
      </div>
    );

  if (!user) {
    return <Redirect to="/login" />;
  }

  const displayName =
    user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email;

  return (
    <div data-testid="layout-root" className={styles['layout']}>
      <AdminAppSidebar
        open={isSidebarOpen}
        appName="Admin"
        currentPath={pathname}
        onSidebarClick={closeSideBar}
      />
      <div className={styles['right-container']}>
        <Header
          name={displayName}
          userRole="admin"
          switchAppName="Hub"
          onSwitchApp={() => {
            globalThis.location.href = globalThis.location.origin.replace('admin', 'hub');
          }}
          isAdminButtonEnabled
          onDrawerToggle={handleDrawerToggle}
          onLogout={signOut}
        />
        <div className={styles['content']}>
          <div className={styles['inner-content']}>
            <Switch>
              <Route exact path="/" component={Dashboard} />
              <Route exact path="/brands/:brandId" component={Brand} />
              <ProtectedRoute
                exact
                path="/users"
                component={Users}
                hasPermission={canViewUsersAndRoles}
              />
              <ProtectedRoute
                exact
                path="/users/:userId"
                component={User}
                hasPermission={canViewUsersAndRoles}
              />
              <ProtectedRoute exact path="/roles" component={Roles} hasPermission={canViewRoles} />
              <Redirect to="/" />
            </Switch>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Layout;
